import { useMemo, useState } from "react";
import {
    Search, Wallet, CreditCard, Smartphone, Landmark, CheckCircle2,
    XCircle, Clock3, IndianRupee, TrendingUp, Download, ChevronDown, Calendar,
} from "lucide-react";

/* ---------------------------------------------------------
   Design tokens
--------------------------------------------------------- */
const C = {
    bg: "#F4F5FA",
    card: "#FFFFFF",
    border: "#E7E8F0",
    indigo: "#5B4FE5",
    indigoSoft: "#EDEBFC",
    indigoDeep: "#443AC4",
    green: "#1E9E6B",
    greenSoft: "#E3F7EE",
    red: "#E1483F",
    redSoft: "#FCEAE9",
    amber: "#C6820E",
    amberSoft: "#FBF0DC",
    blue: "#3B6FE0",
    blueSoft: "#E8EEFC",
    ink: "#1B1D2A",
    slate: "#6B7280",
    slateLight: "#9CA3AF",
};

const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'Courier New', monospace";

/* ---------------------------------------------------------
   Sample data
--------------------------------------------------------- */
const AVATAR_COLORS = ["#5B4FE5", "#3B6FE0", "#1E9E6B", "#C6820E", "#E1483F", "#7C5CE0"];

const RAW = [
    { user: "Dr. Ananya Reddy", course: "Additive Manufacturing", date: "23 Jul 2026", time: "6:00 PM – 7:00 PM", method: "card", amount: 1500, status: "completed" },
    { user: "Prof. Karthik Iyer", course: "Robotics & Mechatronics", date: "30 Jul 2026", time: "5:30 PM – 6:15 PM", method: "upi", amount: 1800, status: "upcoming" },
    { user: "Rahul Varma", course: "EV Powertrain", date: "2 Aug 2026", time: "4:00 PM – 5:00 PM", method: "netbanking", amount: 2200, status: "confirmed" },
    { user: "Sneha Kulkarni", course: "Data Structures Clinic", date: "22 Jul 2026", time: "11:00 AM – 12:00 PM", method: "wallet", amount: 900, status: "completed" },
    { user: "Vikram Nair", course: "Semiconductor Basics", date: "15 Jul 2026", time: "3:00 PM – 4:00 PM", method: "card", amount: 2600, status: "cancelled" },
    { user: "Meera Pillai", course: "UX Research Methods", date: "23 Jul 2026", time: "10:00 AM – 11:00 AM", method: "upi", amount: 1200, status: "completed" },
    { user: "Arjun Menon", course: "Cloud Architecture", date: "5 Aug 2026", time: "6:30 PM – 7:30 PM", method: "wallet", amount: 2000, status: "upcoming" },
    { user: "Divya Shah", course: "Battery Chemistry 101", date: "9 Jul 2026", time: "2:00 PM – 3:00 PM", method: "card", amount: 1700, status: "cancelled" },
    { user: "Rohan Bhatt", course: "Structural Analysis", date: "22 Jul 2026", time: "9:00 AM – 10:00 AM", method: "netbanking", amount: 1400, status: "completed" },
    { user: "Ishaan Kapoor", course: "Thermal Systems Design", date: "1 Jul 2026", time: "1:00 PM – 2:00 PM", method: "upi", amount: 1600, status: "completed" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseDate(str) {
    const [d, mon, y] = str.split(" ");
    return new Date(Number(y), MONTHS.indexOf(mon), Number(d));
}

// Fixed reference point so the demo filters behave predictably.
const TODAY = new Date(2026, 6, 23);

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const METHOD_META = {
    card: { label: "Card", icon: CreditCard },
    upi: { label: "UPI / Online", icon: Smartphone },
    wallet: { label: "Wallet", icon: Wallet },
    netbanking: { label: "Net Banking", icon: Landmark },
};

const STATUS_META = {
    completed: { label: "Completed", color: C.green, soft: C.greenSoft },
    upcoming: { label: "Upcoming", color: C.blue, soft: C.blueSoft },
    confirmed: { label: "Confirmed", color: C.indigo, soft: C.indigoSoft },
    cancelled: { label: "Cancelled", color: C.red, soft: C.redSoft },
};

function initials(name) {
    return name.replace(/^(Dr\.|Prof\.)\s*/i, "").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function fmtAmount(n) {
    return n.toLocaleString("en-IN");
}

/* ---------------------------------------------------------
   Small building blocks
--------------------------------------------------------- */
function StatCard({ label, value, icon: Icon, tint, sub }) {
    return (
        <div
            style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: "14px",
                padding: "20px 22px",
                flex: 1,
                minWidth: "190px",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <span style={{ fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, letterSpacing: "0.01em" }}>
                    {label}
                </span>
                <div
                    style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "8px",
                        background: tint.soft,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon size={15} color={tint.color} />
                </div>
            </div>
            <div style={{ fontFamily: MONO, fontSize: "26px", fontWeight: 700, color: C.ink, letterSpacing: "-0.01em" }}>
                {value}
            </div>
            {sub && (
                <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight, marginTop: "5px" }}>{sub}</div>
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status];
    const icons = { completed: CheckCircle2, upcoming: Clock3, confirmed: Clock3, cancelled: XCircle };
    const Icon = icons[status];
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                fontFamily: SANS,
                fontSize: "12px",
                fontWeight: 600,
                color: meta.color,
                background: meta.soft,
                padding: "5px 11px",
                borderRadius: "999px",
            }}
        >
            <Icon size={12} /> {meta.label}
        </span>
    );
}

function MethodTag({ method }) {
    const meta = METHOD_META[method];
    const Icon = meta.icon;
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", fontFamily: SANS, fontSize: "13px", color: C.ink }}>
            <span
                style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "7px",
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Icon size={13} color={C.slate} />
            </span>
            {meta.label}
        </span>
    );
}

/* ---------------------------------------------------------
   Payment method breakdown card
--------------------------------------------------------- */
function MethodBreakdown({ data }) {
    const totals = useMemo(() => {
        const byMethod = {};
        let sum = 0;
        data.forEach((r) => {
            byMethod[r.method] = (byMethod[r.method] || 0) + r.amount;
            sum += r.amount;
        });
        return { byMethod, sum };
    }, [data]);

    const order = ["card", "upi", "wallet", "netbanking"];
    const colors = { card: C.indigo, upi: C.blue, wallet: C.green, netbanking: C.amber };

    return (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "20px 22px", flex: 1, minWidth: "260px" }}>
            <div style={{ fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, marginBottom: "16px" }}>
                PAYMENT METHODS
            </div>
            <div style={{ display: "flex", height: "9px", borderRadius: "999px", overflow: "hidden", marginBottom: "16px" }}>
                {order.map((m) => {
                    const pct = totals.sum ? (totals.byMethod[m] || 0) / totals.sum * 100 : 0;
                    return pct > 0 ? <div key={m} style={{ width: `${pct}%`, background: colors[m] }} /> : null;
                })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                {order.map((m) => {
                    const amt = totals.byMethod[m] || 0;
                    const pct = totals.sum ? Math.round((amt / totals.sum) * 100) : 0;
                    return (
                        <div key={m} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: colors[m] }} />
                                <span style={{ fontFamily: SANS, fontSize: "12.5px", color: C.ink }}>{METHOD_META[m].label}</span>
                            </div>
                            <span style={{ fontFamily: MONO, fontSize: "12.5px", color: C.slate }}>
                                ₹{fmtAmount(amt)} · {pct}%
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------------------------------------------------------
   Main dashboard
--------------------------------------------------------- */
const TABS = [
    { key: "all", label: "All" },
    { key: "completed", label: "Completed" },
    { key: "upcoming", label: "Upcoming" },
    { key: "confirmed", label: "Confirmed" },
    { key: "cancelled", label: "Cancelled" },
];

export default function PaymentDashboard() {
    const [tab, setTab] = useState("all");
    const [methodFilter, setMethodFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [dateFilter, setDateFilter] = useState("all");
    const [showCustom, setShowCustom] = useState(false);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    const yesterday = useMemo(() => {
        const d = new Date(TODAY);
        d.setDate(d.getDate() - 1);
        return d;
    }, []);

    const passesDateFilter = (r) => {
        const d = parseDate(r.date);
        if (dateFilter === "today") return sameDay(d, TODAY);
        if (dateFilter === "yesterday") return sameDay(d, yesterday);
        if (dateFilter === "month") return d.getFullYear() === TODAY.getFullYear() && d.getMonth() === TODAY.getMonth();
        if (dateFilter === "custom") {
            if (!customFrom && !customTo) return true;
            const from = customFrom ? new Date(customFrom) : null;
            const to = customTo ? new Date(customTo) : null;
            if (from && d < from) return false;
            if (to && d > to) return false;
            return true;
        }
        return true;
    };

    const filtered = useMemo(() => {
        return RAW.filter((r) => {
            if (tab !== "all" && r.status !== tab) return false;
            if (methodFilter !== "all" && r.method !== methodFilter) return false;
            if (query && !`${r.user} ${r.course}`.toLowerCase().includes(query.toLowerCase())) return false;
            if (!passesDateFilter(r)) return false;
            return true;
        });
    }, [tab, methodFilter, query, dateFilter, customFrom, customTo]);

    const DATE_TABS = [
        { key: "all", label: "All time" },
        { key: "today", label: "Today" },
        { key: "yesterday", label: "Yesterday" },
        { key: "month", label: "This month" },
    ];

    const dateLabel =
        dateFilter === "custom"
            ? customFrom || customTo
                ? `${customFrom || "…"} → ${customTo || "…"}`
                : "Custom range"
            : DATE_TABS.find((d) => d.key === dateFilter)?.label;

    const stats = useMemo(() => {
        const paid = RAW.filter((r) => r.status !== "cancelled").reduce((s, r) => s + r.amount, 0);
        const completed = RAW.filter((r) => r.status === "completed").length;
        const cancelled = RAW.filter((r) => r.status === "cancelled").length;
        const upcoming = RAW.filter((r) => r.status === "upcoming" || r.status === "confirmed").length;
        return { paid, completed, cancelled, upcoming };
    }, []);

    return (
        <div style={{ height: "100%", overflowY: "auto", background: C.bg, padding: "36px 24px 60px", fontFamily: SANS, boxSizing: "border-box" }}>
            <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
                    <div>
                        <div style={{ fontFamily: SANS, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", color: C.indigo, marginBottom: "6px" }}>
                            PAYMENTS
                        </div>
                        <h1 style={{ fontFamily: SANS, fontSize: "26px", fontWeight: 700, color: C.ink, margin: 0 }}>
                            Payment dashboard
                        </h1>
                    </div>
                    <button
                        style={{
                            display: "flex", alignItems: "center", gap: "7px",
                            fontFamily: SANS, fontWeight: 600, fontSize: "13px", color: C.ink,
                            background: C.card, border: `1px solid ${C.border}`, borderRadius: "8px",
                            padding: "10px 16px", cursor: "pointer",
                        }}
                    >
                        <Download size={14} /> Export
                    </button>
                </div>

                {/* Stat cards */}
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                    <StatCard label="TOTAL PAID" value={`₹${fmtAmount(stats.paid)}`} icon={IndianRupee} tint={{ color: C.indigo, soft: C.indigoSoft }} sub={`${RAW.length} transactions`} />
                    <StatCard label="COMPLETED" value={stats.completed} icon={CheckCircle2} tint={{ color: C.green, soft: C.greenSoft }} sub="Payments settled" />
                    <StatCard label="UPCOMING / CONFIRMED" value={stats.upcoming} icon={Clock3} tint={{ color: C.blue, soft: C.blueSoft }} sub="Scheduled sessions" />
                    <StatCard label="CANCELLED" value={stats.cancelled} icon={XCircle} tint={{ color: C.red, soft: C.redSoft }} sub="Refund issued" />
                </div>

                {/* Method breakdown + trend strip */}
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
                    <MethodBreakdown data={RAW} />
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", padding: "20px 22px", flex: 1, minWidth: "260px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <div style={{ fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, marginBottom: "10px" }}>
                            THIS MONTH
                        </div>
                        <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                            <span style={{ fontFamily: MONO, fontSize: "26px", fontWeight: 700, color: C.ink }}>
                                ₹{fmtAmount(stats.paid)}
                            </span>
                            <span style={{ display: "flex", alignItems: "center", gap: "3px", fontFamily: SANS, fontSize: "12px", fontWeight: 700, color: C.green }}>
                                <TrendingUp size={13} /> 12.4%
                            </span>
                        </div>
                        <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight, marginTop: "6px" }}>
                            vs ₹{fmtAmount(Math.round(stats.paid * 0.89))} last month
                        </div>
                    </div>
                </div>

                {/* Filters + table */}
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "14px", overflow: "hidden" }}>
                    {/* Date filter row */}
                    <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "6px", fontFamily: SANS, fontSize: "12.5px", fontWeight: 600, color: C.slate, marginRight: "4px" }}>
                                <Calendar size={14} color={C.slateLight} /> Date
                            </span>
                            {DATE_TABS.map((d) => (
                                <button
                                    key={d.key}
                                    onClick={() => { setDateFilter(d.key); setShowCustom(false); }}
                                    style={{
                                        fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                                        padding: "7px 13px", borderRadius: "999px", cursor: "pointer",
                                        border: `1px solid ${dateFilter === d.key ? C.indigo : C.border}`,
                                        color: dateFilter === d.key ? C.indigo : C.slate,
                                        background: dateFilter === d.key ? C.indigoSoft : "transparent",
                                    }}
                                >
                                    {d.label}
                                </button>
                            ))}
                            <button
                                onClick={() => { setDateFilter("custom"); setShowCustom((s) => !s); }}
                                style={{
                                    fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                                    padding: "7px 13px", borderRadius: "999px", cursor: "pointer",
                                    border: `1px solid ${dateFilter === "custom" ? C.indigo : C.border}`,
                                    color: dateFilter === "custom" ? C.indigo : C.slate,
                                    background: dateFilter === "custom" ? C.indigoSoft : "transparent",
                                }}
                            >
                                Custom date {dateFilter === "custom" ? `· ${dateLabel}` : ""}
                            </button>

                            {dateFilter !== "all" && (
                                <button
                                    onClick={() => { setDateFilter("all"); setShowCustom(false); setCustomFrom(""); setCustomTo(""); }}
                                    style={{
                                        fontFamily: SANS, fontSize: "12px", fontWeight: 600, color: C.slateLight,
                                        background: "none", border: "none", cursor: "pointer", marginLeft: "2px",
                                    }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        {showCustom && dateFilter === "custom" && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                                <div>
                                    <label style={{ display: "block", fontFamily: SANS, fontSize: "11px", fontWeight: 600, color: C.slateLight, marginBottom: "4px" }}>
                                        FROM
                                    </label>
                                    <input
                                        type="date"
                                        value={customFrom}
                                        onChange={(e) => setCustomFrom(e.target.value)}
                                        style={{
                                            fontFamily: SANS, fontSize: "12.5px", padding: "8px 10px",
                                            borderRadius: "7px", border: `1px solid ${C.border}`, background: C.bg, outline: "none",
                                        }}
                                    />
                                </div>
                                <div style={{ color: C.slateLight, marginTop: "18px" }}>→</div>
                                <div>
                                    <label style={{ display: "block", fontFamily: SANS, fontSize: "11px", fontWeight: 600, color: C.slateLight, marginBottom: "4px" }}>
                                        TO
                                    </label>
                                    <input
                                        type="date"
                                        value={customTo}
                                        onChange={(e) => setCustomTo(e.target.value)}
                                        style={{
                                            fontFamily: SANS, fontSize: "12.5px", padding: "8px 10px",
                                            borderRadius: "7px", border: `1px solid ${C.border}`, background: C.bg, outline: "none",
                                        }}
                                    />
                                </div>
                                <button
                                    onClick={() => setShowCustom(false)}
                                    style={{
                                        fontFamily: SANS, fontSize: "12.5px", fontWeight: 700, color: "#fff",
                                        background: C.indigo, border: "none", borderRadius: "7px",
                                        padding: "9px 16px", cursor: "pointer", marginTop: "18px",
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    style={{
                                        fontFamily: SANS, fontSize: "13px", fontWeight: 600,
                                        padding: "8px 14px", borderRadius: "8px", border: "none", cursor: "pointer",
                                        color: tab === t.key ? "#fff" : C.slate,
                                        background: tab === t.key ? C.indigo : "transparent",
                                    }}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            <div style={{ position: "relative" }}>
                                <Search size={14} color={C.slateLight} style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)" }} />
                                <input
                                    placeholder="Search mentor or course..."
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    style={{
                                        fontFamily: SANS, fontSize: "13px", padding: "9px 12px 9px 32px",
                                        borderRadius: "8px", border: `1px solid ${C.border}`, background: C.bg,
                                        outline: "none", width: "200px",
                                    }}
                                />
                            </div>
                            <div style={{ position: "relative" }}>
                                <select
                                    value={methodFilter}
                                    onChange={(e) => setMethodFilter(e.target.value)}
                                    style={{
                                        fontFamily: SANS, fontSize: "13px", padding: "9px 30px 9px 12px",
                                        borderRadius: "8px", border: `1px solid ${C.border}`, background: C.bg,
                                        outline: "none", appearance: "none", color: C.ink, cursor: "pointer",
                                    }}
                                >
                                    <option value="all">All methods</option>
                                    {Object.entries(METHOD_META).map(([k, v]) => (
                                        <option key={k} value={k}>{v.label}</option>
                                    ))}
                                </select>
                                <ChevronDown size={13} color={C.slateLight} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                            </div>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "820px" }}>
                            <thead>
                                <tr>
                                    {["MENTOR / COURSE", "DATE & TIME", "METHOD", "AMOUNT", "STATUS", "ACTION"].map((h, i) => (
                                        <th
                                            key={h}
                                            style={{
                                                textAlign: i >= 3 && i <= 4 ? (i === 3 ? "right" : "left") : "left",
                                                fontFamily: SANS, fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.06em",
                                                color: C.slate, padding: "13px 20px", borderBottom: `1px solid ${C.border}`,
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} style={{ padding: "40px 20px", textAlign: "center", fontFamily: SANS, fontSize: "13.5px", color: C.slateLight }}>
                                            No transactions match these filters.
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((r, i) => {
                                    const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
                                    return (
                                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                    <div
                                                        style={{
                                                            width: "34px", height: "34px", borderRadius: "50%",
                                                            background: color, color: "#fff", display: "flex",
                                                            alignItems: "center", justifyContent: "center",
                                                            fontFamily: SANS, fontSize: "12px", fontWeight: 700, flexShrink: 0,
                                                        }}
                                                    >
                                                        {initials(r.user)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontFamily: SANS, fontSize: "13.5px", fontWeight: 600, color: C.ink }}>{r.user}</div>
                                                        <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight }}>{r.course}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontFamily: SANS, fontSize: "13px", color: C.ink }}>{r.date}</div>
                                                <div style={{ fontFamily: SANS, fontSize: "12px", color: C.slateLight }}>{r.time}</div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <MethodTag method={r.method} />
                                            </td>
                                            <td style={{ padding: "14px 20px", textAlign: "right", fontFamily: MONO, fontSize: "13.5px", fontWeight: 700, color: C.ink }}>
                                                ₹{fmtAmount(r.amount)}
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <StatusBadge status={r.status} />
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                {r.status === "cancelled" ? (
                                                    <span style={{ fontFamily: SANS, fontSize: "12.5px", color: C.slateLight }}>Refunded</span>
                                                ) : r.status === "completed" ? (
                                                    <button
                                                        style={{
                                                            fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                                                            color: C.indigo, background: C.indigoSoft, border: "none",
                                                            borderRadius: "7px", padding: "7px 12px", cursor: "pointer",
                                                        }}
                                                    >
                                                        View receipt
                                                    </button>
                                                ) : (
                                                    <button
                                                        style={{
                                                            fontFamily: SANS, fontSize: "12.5px", fontWeight: 600,
                                                            color: C.red, background: "#fff", border: `1px solid ${C.redSoft}`,
                                                            borderRadius: "7px", padding: "7px 12px", cursor: "pointer",
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}